import React, { useContext, useRef, useState } from "react";
import classNames from "classnames";
import { CircularProgress } from "@material-ui/core";
import { AssetRecordType, createShapeId, Editor, TLAssetId, TLShapeId, TLShapePartial } from "@tldraw/tldraw";
import { SnackBarContext } from "components/snack-bar";
import {
  AssetSpec,
  parseSvg,
  planShapes,
  ShapePlan,
  SVG_IMPORT_LIMITS,
  SvgImportError,
  TargetBox,
} from "../../utils/svgImport";
import { applyFontFallbacks, installEmbeddedFonts, resolveFontTeam } from "./installFonts";

export { SVG_IMPORT_FLAG, useSvgImportEnabled } from "./useSvgImportEnabled";

interface ImportSvgButtonProps {
  readonly editor: Editor | null;
  readonly onAssetCreate?: (file: File, id: string) => Promise<string | false>;
  readonly designId?: string;
}

const CANVAS_IDS = ["shape:front", "shape:back"] as const;
const VIEWPORT_FILL = 0.8;
const MAX_LISTED_WARNINGS = 3;
const TEXT_WIDTH_SLACK = 0.15; // of the font size

// The side of the flyer under the viewport centre, else the middle 80% of the viewport.
const findTargetBox = (editor: Editor): TargetBox => {
  const viewport = editor.getViewportPageBounds();
  const centre = { x: viewport.x + viewport.w / 2, y: viewport.y + viewport.h / 2 };
  const sides = CANVAS_IDS.map((id) => editor.getShapePageBounds(id as TLShapeId)).filter(Boolean);
  const hit = sides.find(
    (b) => b && centre.x >= b.minX && centre.x <= b.maxX && centre.y >= b.minY && centre.y <= b.maxY
  );
  const side = hit ?? sides[0];
  if (side) return { x: side.x, y: side.y, w: side.w, h: side.h };
  return {
    x: viewport.x + (viewport.w * (1 - VIEWPORT_FILL)) / 2,
    y: viewport.y + (viewport.h * (1 - VIEWPORT_FILL)) / 2,
    w: viewport.w * VIEWPORT_FILL,
    h: viewport.h * VIEWPORT_FILL,
  };
};

const dataUrlToFile = (asset: AssetSpec, name: string): File => {
  const base64 = asset.dataUrl.slice(asset.dataUrl.indexOf(",") + 1);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], name, { type: asset.mimeType });
};

// Embedded images go through the builder's normal upload (S3); the data URL is only a fallback.
const resolveAssetSources = async (
  assets: readonly AssetSpec[],
  onAssetCreate: ImportSvgButtonProps["onAssetCreate"]
): Promise<string[]> =>
  Promise.all(
    assets.map(async (asset, index) => {
      if (!onAssetCreate) return asset.dataUrl;
      try {
        const extension = asset.mimeType.split("/")[1] ?? "png";
        const uploaded = await onAssetCreate(dataUrlToFile(asset, `svg-import-${index}.${extension}`), asset.id);
        return uploaded || asset.dataUrl;
      } catch (error) {
        console.error("SVG import: image upload failed, keeping it embedded", error);
        return asset.dataUrl;
      }
    })
  );

const FONT_LOAD_TIMEOUT_MS = 3000;

// Text is measured on create; a face that isn't loaded yet (e.g. italic) measures in a fallback font.
const preloadFonts = async (plan: ShapePlan): Promise<void> => {
  const fonts = globalThis.document?.fonts;
  if (!fonts) return;
  const specs = new Set(
    plan.shapes
      .filter((s) => s.type === "customText")
      .map((s) => `${s.props.italic} ${s.props.fontWeight} ${s.props.size}px "${s.props.font}"`)
  );
  const loads = Promise.all(Array.from(specs).map((spec) => fonts.load(spec).catch(() => [])));
  await Promise.race([loads, new Promise((resolve) => setTimeout(resolve, FONT_LOAD_TIMEOUT_MS))]);
};

const applyPlan = (editor: Editor, plan: ShapePlan, sources: readonly string[]): TLShapeId[] => {
  const grouped = new Set(plan.groups.flatMap((g) => g.childIds));
  const realIds = new Map<string, string>();
  editor.markHistoryStoppingPoint("import-svg");
  editor.run(() => {
    if (plan.assets.length > 0) {
      editor.createAssets(
        plan.assets.map((asset, index) => ({
          id: asset.id as TLAssetId,
          type: "image" as const,
          typeName: "asset" as const,
          props: {
            name: `svg-import-${index}`,
            src: sources[index],
            w: asset.w,
            h: asset.h,
            mimeType: asset.mimeType,
            isAnimated: false,
          },
          meta: {},
        }))
      );
    }
    editor.createShapes(plan.shapes.map((shape) => ({ ...shape, id: shape.id as TLShapeId } as TLShapePartial)));
    // The builder's editor ignores groupShapes' groupId, so map planned ids to the ids actually created.
    plan.groups.forEach((group) => {
      const childIds = group.childIds.map((id) => realIds.get(id) ?? id) as TLShapeId[];
      editor.groupShapes(childIds, { groupId: group.id as TLShapeId, select: false });
      const parentId = editor.getShape(childIds[0])?.parentId;
      if (parentId && parentId !== group.id && editor.getShape(parentId as TLShapeId)?.type === "group") {
        realIds.set(group.id, parentId);
      }
    });
    // autoSize text wraps a pixel short on italics/emoji; freeze it at the measured width plus slack,
    // the fixed-width mode the builder's own Text tool uses.
    plan.shapes
      .filter((spec) => spec.type === "customText")
      .forEach((spec) => {
        const id = spec.id as TLShapeId;
        const width = editor.getShapeGeometry(id).bounds.w;
        const slack = Math.ceil(Number(spec.props.size) * TEXT_WIDTH_SLACK);
        editor.updateShape({ id, type: "customText", props: { autoSize: false, w: Math.ceil(width) + slack } });
      });
    // SVG text-anchor positions by the anchor point; now that text has real widths, re-anchor it.
    plan.textAnchors.forEach(({ id, align, anchorX }) => {
      const shape = editor.getShape(id as TLShapeId);
      const bounds = editor.getShapePageBounds(id as TLShapeId);
      if (!shape || !bounds) return;
      const offset = align === "middle" ? bounds.w / 2 : bounds.w;
      editor.updateShape({ id: shape.id, type: shape.type, x: anchorX - offset });
    });
  });
  const topLevel = [...plan.shapes.map((s) => s.id), ...plan.groups.map((g) => g.id)].filter((id) => !grouped.has(id));
  return topLevel.map((id) => realIds.get(id) ?? id) as TLShapeId[];
};

const summarise = (count: number, warnings: readonly string[]): string => {
  const base = `Imported ${count} element${count === 1 ? "" : "s"} from SVG.`;
  if (warnings.length === 0) return base;
  const listed = warnings.slice(0, MAX_LISTED_WARNINGS).join("; ");
  const more = warnings.length > MAX_LISTED_WARNINGS ? ` (+${warnings.length - MAX_LISTED_WARNINGS} more)` : "";
  return `${base} Simplified: ${listed}${more}.`;
};

const ImportSvgButton = ({ editor, onAssetCreate, designId }: ImportSvgButtonProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const runSnackBar = useContext(SnackBarContext);
  const isIntegrated = new URLSearchParams(window.location.search).get("variant") === "integrated";

  const notify = (msg: string, type: "success" | "error" | "warning"): void =>
    runSnackBar({ msg, type, vertical: "top", horizontal: "center" });

  const importFile = async (file: File): Promise<void> => {
    if (!editor) return;
    if (file.size > SVG_IMPORT_LIMITS.maxBytes) {
      notify("This SVG is too large to import (max 10 MB).", "error");
      return;
    }
    const parsed = parseSvg(await file.text());
    const planned = planShapes(parsed, findTargetBox(editor), {
      shape: () => createShapeId(),
      asset: () => AssetRecordType.createId(),
    });
    if (planned.shapes.length === 0) {
      notify("Nothing to import: the SVG has no visible shapes, text or images.", "warning");
      return;
    }
    const [sources, installedFonts] = await Promise.all([
      resolveAssetSources(planned.assets, onAssetCreate),
      resolveFontTeam(planned.fonts.length > 0 ? designId : undefined).then((team) =>
        installEmbeddedFonts(planned.fonts, team)
      ),
    ]);
    const plan = applyFontFallbacks(planned, installedFonts);
    await preloadFonts(plan);
    const topLevel = applyPlan(editor, plan, sources);
    editor.setCurrentTool("select");
    editor.select(...topLevel);
    notify(summarise(plan.shapes.length, parsed.warnings), parsed.warnings.length ? "warning" : "success");
  };

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-importing the same file
    if (!file) return;
    setBusy(true);
    try {
      await importFile(file);
    } catch (error) {
      const message = error instanceof SvgImportError ? error.message : "Could not import this SVG.";
      if (!(error instanceof SvgImportError)) console.error("SVG import failed", error);
      notify(message, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      title="Import an SVG as editable shapes"
      data-cypress-id="import-svg-button"
      onClick={() => !busy && editor && inputRef.current?.click()}
      onKeyDown={(e) => e.key === "Enter" && !busy && inputRef.current?.click()}
      className={classNames(
        "flex items-center justify-center font-bold text-base leading-5 transition-all duration-300 mx-1.5 bg-white text-[#1a2547]",
        isIntegrated
          ? "rounded-[12px] border border-solid border-[#1a2547] h-[40px] w-[124px]"
          : "border-2 border-solid border-[#1a2547] rounded-2xl h-12 shadow-lg w-[148px]",
        busy || !editor ? "cursor-wait opacity-60" : "cursor-pointer hover:scale-[1.02]"
      )}
    >
      {busy ? <CircularProgress size={20} style={{ color: "#1a2547" }} /> : "Import SVG"}
      <input
        ref={inputRef}
        type="file"
        accept=".svg,image/svg+xml"
        className="hidden"
        data-cypress-id="import-svg-input"
        onChange={onChange}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

export default ImportSvgButton;
